import type {
  FrontKind,
  FurniturePart,
  MaterialId,
  ParamValue,
  TemplateId,
  Vec2,
} from "../../types";

/** Inspector-facing description of one template parameter. */
export type ParamDef =
  | {
      id: string;
      label: string;
      type: "enum";
      options: { value: string; label: string }[];
      default: string;
      /** Only show when another param has a given value. */
      visibleWhen?: { param: string; equals: ParamValue };
      /** Canvas-only param. Kept out of the Shape inspector. */
      hidden?: boolean;
    }
  | {
      id: string;
      label: string;
      type: "int";
      min: number;
      max: number;
      step?: number;
      default: number;
      visibleWhen?: { param: string; equals: ParamValue };
      /** Canvas-only param. Kept out of the Shape inspector. */
      hidden?: boolean;
    }
  | {
      id: string;
      label: string;
      type: "bool";
      default: boolean;
      visibleWhen?: { param: string; equals: ParamValue };
      /** Canvas-only param. Kept out of the Shape inspector. */
      hidden?: boolean;
    };

export interface BuildContext {
  /** Footprint width (x extent), inches. */
  w: number;
  /** Footprint depth (y extent), inches. */
  d: number;
  /** Piece height, inches. */
  h: number;
  /** Resolved params (defaults merged with the piece's values). */
  params: Record<string, ParamValue>;
}

export interface BuildResult {
  parts: FurniturePart[];
  /** Explicit footprint outline. When omitted the footprint is the bbox
   *  rectangle. Templates with non-rectangular footprints (sectionals,
   *  round tables) must supply this so geometry uses the true outline. */
  footprint?: Vec2[];
}

export interface FurnitureTemplate {
  id: TemplateId;
  label: string;
  params: ParamDef[];
  frontKind: FrontKind;
  defaultMaterial: MaterialId;
  build: (ctx: BuildContext) => BuildResult;
}

/** Merge a template's defaults with (possibly partial / stale) stored params. */
export function resolveParams(
  template: FurnitureTemplate,
  params: Record<string, ParamValue> | undefined
): Record<string, ParamValue> {
  const out: Record<string, ParamValue> = {};
  for (const def of template.params) {
    const raw = params?.[def.id];
    switch (def.type) {
      case "enum":
        out[def.id] =
          typeof raw === "string" && def.options.some((o) => o.value === raw)
            ? raw
            : def.default;
        break;
      case "int": {
        const n = typeof raw === "number" && Number.isFinite(raw) ? Math.round(raw) : def.default;
        out[def.id] = Math.min(def.max, Math.max(def.min, n));
        break;
      }
      case "bool":
        out[def.id] = typeof raw === "boolean" ? raw : def.default;
        break;
    }
  }
  return out;
}

export function num(params: Record<string, ParamValue>, id: string, fallback: number): number {
  const v = params[id];
  return typeof v === "number" ? v : fallback;
}

export function str(params: Record<string, ParamValue>, id: string, fallback: string): string {
  const v = params[id];
  return typeof v === "string" ? v : fallback;
}

export function bool(params: Record<string, ParamValue>, id: string, fallback: boolean): boolean {
  const v = params[id];
  return typeof v === "boolean" ? v : fallback;
}
